<template>
  <div class="conversation-chat-container" ref="convoChatContainerRef">
    <b-overlay :show="isLoading" rounded="sm">
      <div class="card">
        <div class="chat-header border d-flex justify-content-between align-items-center px-2">
          <h3 class="m-0">
            {{
              currentConversation.label
                ? prettyPhoneNumber(currentConversation.label)
                : prettyPhoneNumber(currentConversation.primary_recipient)
            }}
          </h3>
          <div class="d-flex align-items-center gap-1">
            <span :class="`convo-${currentConversation.status} badge font-weight-medium`">
              {{ currentConversation.status }}
            </span>
            <span :class="`convo-${currentConversation.status} badge font-weight-medium`">
              {{ currentConversation.channel }}
            </span>
          </div>
        </div>
        <div
          class="chat-box card-body border border-top-0 px-0 d-flex flex-column gap-4"
          :class="{
            'bg-light': isDarkMode,
          }"
          :style="{
            height: `${chatBoxHeight}px`
          }"
        >
          <p
            v-for="message in mainConversationMessages"
            :key="message.message_id"
            class="mb-0"
            :class="{
              'mr-auto': message.direction != 'outbound',
              'ml-auto': message.direction == 'outbound' && message.source != 'note',
              'w-50': !(message.direction == 'outbound' && message.source == 'note')
            }"
          >
            <ul class="chat-list chat d-block">
              <!--chat Row -- OUTBOUND -->
              <li v-if="message.direction == 'outbound'" class="d-block">
                <div
                  v-if="message.source === 'note'"
                  class="px-2 py-2"
                >
                  <div class="box d-inline-block w-100 text-center font-italic fs-2">
                    {{ message.message_body }}
                    <p class="fs-1 mb-0">{{ formatTimestamp(message.created_at, "HH:mm:ss") }}</p>
                  </div>
                </div>

                <div v-else class="chat-content d-flex px-2 py-2 gap-1">
                  <div class="d-flex flex-column gap-1">
                    <div class="d-flex align-items-baseline gap-2">
                      <h5 class="fs-3 text-muted font-weight-bold mx-0 my-0 text-nowrap">
                        {{ message.operator_id ? message.operator : "System Generated Message" }}
                      </h5>
                      <div v-if="message.source === 'sms' || message.source === 'voice'" class="fs-2 text-nowrap">
                        {{ formatTimestamp(message.created_at, "LT") }}
                      </div>
                      <div
                        v-if="message.source === 'sms' || message.source === 'voice'"
                        class="fs-2"
                        :class="`${message.error_code ? 'text-danger' : ''}`"
                      >
                        <span :title="`${message.error_message ? message.error_message : ''}`">
                          {{ message.status }}
                        </span>
                      </div>
                    </div>
                    <div
                      class="box d-inline-block text-dark font-weight-medium fs-3 w-100 p-2 rounded"
                      :class="{
                        'bg-light-inverse': !isDarkMode,
                        'bg-dark': isDarkMode,
                      }"
                    >
                      <div
                        v-linkified
                        class="fw-normal"
                        style="
                          white-space: pre-line;
                          word-wrap: break-word;
                        "
                      >
                        {{ message.message_body }}
                      </div>
                      <div
                        v-if="
                          message.body_transl &&
                          message.language &&
                          message.language.substring(0, 2) != 'en'
                        "
                        class="mt-2"
                      >
                        <a
                          data-toggle="collapse"
                          :href="`#id-${message.message_id}_transl`"
                          role="button"
                          aria-expanded="true"
                          :aria-controls="`id-${message.message_id}_transl`"
                          class="font-weight-light chat-message-translated-lang text-dark"
                        >
                          <font-awesome-icon icon="fas fa-caret-right" />
                          {{
                            ['sms', 'chat', 'voice'].includes(message.source)
                              ? `Sent in ${dialectLabel(message.language)}`
                              : `Translated to ${dialectLabel(message.language)}`
                          }}
                        </a>

                        <div
                          class="collapse multi-collapse show"
                          :id="`id-${message.message_id}_transl`"
                        >
                          <div
                            v-linkified
                            class="fs-2 pl-3 fw-normal"
                            style="
                              white-space: pre-line;
                              word-wrap: break-word;
                            "
                          >
                            {{ message.body_transl }}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div
                      v-if="(message.recipient_number && message.recipient_number != currentConversation.primary_recipient) || message.source === 'voice'"
                      class="d-flex align-items-cetner justify-content-end text-muted fs-2 font-italic gap-3"
                    >
                      <span v-if="message.recipient_number && message.recipient_number != currentConversation.primary_recipient">sent to: {{ prettyPhoneNumber(message.recipient_number) }}</span>
                      <span v-if="message.source === 'voice'">Sent as speech</span>
                    </div>
                  </div>
                </div>
              </li>
              <!--chat Row OUTBOUND-->

              <!--chat Row INBOUND -->
              <li v-else>
                <div class="chat-content d-flex px-2 py-2 gap-1">
                  <!-- <div class="rounded inbound-avatar"></div> -->
                  <div class="d-flex flex-column gap-1">
                    <div class="d-flex align-items-baseline gap-2">
                      <h5
                        v-if="message.recipient_number || message.recipient_label"
                        class="fs-3 text-muted font-weight-bold mb-0"
                      >
                        {{
                          message.recipient_label
                            ? prettyPhoneNumber(message.recipient_label)
                            : prettyPhoneNumber(message.recipient_number)
                        }}
                      </h5>
                      <div class="fs-2">
                        {{ formatTimestamp(message.created_at, "LT") }}
                      </div>
                    </div>

                    <div
                      class="box d-inline-block text-dark font-weight-medium fs-3 w-100 p-2 rounded"
                      :class="{
                        'bg-light-info': isDarkMode,
                        'bg-light': !isDarkMode,
                      }"
                      :id="`inbound-message-${message.message_id}`"
                    >
                      <div
                        class="fw-normal"
                        style="
                          white-space: pre-line;
                          word-wrap: break-word;
                        "
                        v-linkified
                      >
                        {{ message.message_body }}
                      </div>

                      <div
                        v-if="
                          message.body_transl &&
                          message.body_transl !== message.body &&
                          message.language &&
                          message.language.substring(0, 2) != 'en'
                        "
                        class="mt-2"
                      >
                        <a
                          data-toggle="collapse"
                          :href="`#id-${message.message_id}_transl`"
                          role="button"
                          aria-expanded="true"
                          :aria-controls="`id-${message.message_id}_transl`"
                          class="font-weight-light chat-message-translated-lang text-dark"
                        >
                          <font-awesome-icon icon="fas fa-caret-right" />
                          Translated from {{ dialectLabel(message.language) }}
                        </a>
                        <div class="collapse multi-collapse show" :id="`id-${message.message_id}_transl`">
                          <div
                            class="fw-normal pl-3 fs-2"
                            style="
                              white-space: pre-line;
                              word-wrap: break-word;
                            "
                            v-linkified
                          >
                            {{ message.body }}
                          </div>
                        </div>
                      </div>

                      <div
                        v-if="messageAttachments(message.message_id).length > 0"
                        class="chat-content-attachments d-flex flex-wrap gap-2 mt-2"
                      >
                        <b-spinner v-if="messageAttachments(message.message_id).length == 0" label="Loading..."></b-spinner>
                        <template v-else>
                          <div
                            v-for="attachment in messageAttachments(message.message_id)"
                            :key="attachment.attachment_id"
                            class="chat-content-attachment"
                          >
                            <ImageItem
                              v-if="getAttachmentType(attachment.content_type) === 'image'"
                              :attachmentId="attachment.attachment_id"
                              :attachment="attachment"
                              :processed="attachment.status === 'processed'"
                              :image-width="'200px'"
                            />
                            <MediaItem
                              v-if="getAttachmentType(attachment.content_type) === 'media'"
                              :attachmentId="attachment.attachment_id"
                              :attachment="attachment"
                              :processed="attachment.status === 'processed'"
                              :media-width="'200px'"
                            />
                          </div>
                        </template>
                      </div>
                    </div>
                  </div>
                </div>
              </li>
              <!--chat Row INBOUND -->
            </ul>
          </p>
        </div>
        <v-resizable
          :class="`chat-tabs-resizer ${resizingDrawer ? 'resizing' : ''}`"
          :width="drawerWidth"
          :height="drawerHeight"
          :fitParent="true"
          :top="drawerTop"
          :left="drawerLeft"
          :minWidth="drawerWidth"
          :maxWidth="drawerWidth"
          :minHeight="40"
          :maxHeight="drawerMaxHeight"
          :active="isDetailTabOpened ? ['t'] : []"
          @resize:start="vResizeHandler"
          @resize:move="vResizeHandler"
          @resize:end="vResizeHandler"
        >
          <div
            class="chat-tabs bg-white-custom"
            :class="{
              'chat-tabs-rounded': currentConversation.channel === 'media'
            }"
          >
            <div
              class="chat-tabs-header border border-top-transparent"
              :class="{
                'border-bottom': isDetailTabOpened,
                'chat-tabs-header-rounded': !isDetailTabOpened
              }"
            >
              <div class="chat-tabs-header--trigger bg-light border border-top-transparent"
                @click="toggleConversationDetailTabs">
                <b-icon-chevron-compact-up v-if="!isDetailTabOpened" />
                <b-icon-chevron-compact-down v-else />
              </div>
              <div class="chat-tabs-header--tabs d-flex align-items-center">
                <div
                  v-for="(drawerTab, drawerTabIndex) in getDrawerTabs"
                  :key="`drawer-tab-${drawerTabIndex}`"
                  class="chat-tabs-header--tab"
                  :class="{
                    'tab-selected': detailTabOpened === drawerTabIndex,
                    'border-info': detailTabOpened === drawerTabIndex,
                    'text-info': detailTabOpened === drawerTabIndex,
                  }"
                  @click="openTab(drawerTabIndex)"
                >
                  {{ drawerTab.type === 'attachments' ? mediaTabTitle : drawerTab.title }}
                </div>
                <div 
                  v-if="currentConversation.channel !== 'media' && currentConversation.status === 'active' && chatEnabled"
                  class="chat-tabs-header--quick-reply"
                >
                  <MessageTemplates
                    theme="dropdown"
                    :placeholder="isMobile ? 'Quick Reply' : 'Send Quick Reply Template'"
                    :noItemPlaceholder="isMobile ? 'No Items' : 'No Reply Templates Found'"
                  />
                </div>
              </div>
            </div>
            <div v-show="isDetailTabOpened" class="chat-tabs-content border border-top-transparent">
              <b-tabs :value="detailTabOpened">
                <b-tab v-if="isSDKEnabled && agencySettings.uxVoiceEnabled">
                  <TranscribeTab :conversationId="conversationId" />
                </b-tab>
                <b-tab v-if="isSDKEnabled && agencySettings.uxVideoEnabled">
                  <VideoTab />
                </b-tab>
                <b-tab>
                  <MediaTab :conversationMessages="conversationMessages"
                    :conversationAttachments="conversationAttachments"
                    :opened="isDetailTabOpened && detailTabOpened === mediaTabIndex" />
                </b-tab>
              </b-tabs>
            </div>
          </div>
        </v-resizable>
        <div
          v-if="currentConversation.channel != 'media'"
          class="chat-send-message-footer border border-top-transparent d-flex align-items-center px-2 gap-2"
        >
          <div class="d-flex justify-content-center align-items-center">
            <MessageBox
              ref="messageBox"
              v-model="message"
              :placeholder="inputPlaceholder"
              :disabled="!chatEnabled"
              @send-message="submitMessage"
            />
          </div>
          <div class="d-flex justify-content-center align-items-center">
            <SendMessageCTA
              v-if="chatEnabled"
              v-model="ttsActive"
              :isTTSEnabled="currentConversation.channel === 'voice'"
              :isSending="httpPending"
              @send-message="() => submitMessage(message)"
            />
          </div>
        </div>
      </div>
    </b-overlay>
  </div>
</template>

<script>
/* eslint-disable */
import { mapGetters, mapActions } from "vuex";
import moment from "moment-timezone";
import ImageItem from "../ImageItem";
import MediaItem from "../MediaItem";
import AudioItem from "../AudioItem";
import MessageBox from "./MessageBox.vue";
import formatter from "@/mixins/formatter";
import errorHandler from "@/mixins/errorHandler";
import SocketHolder from "../../SocketHolder";
import MediaTab from './MediaTab';
import TranscribeTab from "./TranscribeTab";
import VideoTab from "./VideoTab";
import MessageTemplates from './MessageTemplates';
import VResizable from "vue-resizable";
import SendMessageCTA from "./SendMessageCTA";

export default {
  name: "ConversationChatContainer",
  mixins: [formatter, errorHandler],
  props: {
    conversationId: {
      type: String,
      required: true,
    },
  },
  components: {
    ImageItem,
    MediaItem,
    AudioItem,
    MessageBox,
    MediaTab,
    TranscribeTab,
    VideoTab,
    MessageTemplates,
    VResizable,
    SendMessageCTA
  },
  data() {
    return {
      httpPending: false,
      isLoading: false,
      message: "",
      inboundTranscribeText: null,
      outboundTranscribeText: null,
      isDetailTabOpened: false,
      detailTabOpened: 0,
      drawerTabs: [
        {
          type: 'voice',
          title: 'Voice',
          enabled: false,
        },
        {
          type: 'video',
          title: 'Video',
          enabled: false,
        },
        {
          type: 'attachments',
          enabled: true,
        },
      ],
      chatBoxHeight: 0,
      drawerTop: 0,
      drawerLeft: 0,
      drawerWidth: 0,
      drawerHeight: 40,
      drawerMaxHeight: 40,
      resizingDrawer: false,
      fetchAbortController: null,
      ttsActive: false,
    };
  },
  computed: {
    ...mapGetters("conversations", [
      "currentConversation",
      "conversationMessages",
      "conversationStatus",
      "latestGeography",
      "conversationGeography",
      "conversationAttachments",
      "lastMessageAt"
    ]),
    ...mapGetters("agency", ["currentAgencyId", "agencySettings", "isSDKEnabled", "textDialects"]),
    ...mapGetters("user", ["currentToken", "currentUser", "isMobile", "isDarkMode", "viewport", "userSidebarOpened"]),
    dialects() {
      return this.textDialects || [];
    },
    chatEnabled() {
      const { status, operator_id } = this.currentConversation;
      const { enableConversationAssignment } = this.agencySettings;
      if (status === "active") {
        if (enableConversationAssignment === true && (operator_id === this.currentUser.user_id || (operator_id !== null && this.$permission("admin:agency"))))
          return true;
        else if (enableConversationAssignment !== true)
          return true;
      }
      return false;
    },
    mainConversationMessages() {
      return this.conversationMessages.filter((value) => value.source !==  'transcribe')
    },
    inputPlaceholder: function () {
      if (!this.currentConversation)
        return;

      if (this.currentConversation.status !== "active")
        return "Conversation is has been closed and can no longer be replied to.";
      else if (this.currentConversation.status === "active" && this.agencySettings.enableConversationAssignment === true && this.currentConversation.operator_id !== this.currentUser.user_id)
        return "You must be assigned as agent on the conversation in order to reply.";
      else if (this.currentConversation.recipients.length == 0)
        return "There are no recipients assigned to this conversation.";

      return "Enter message to send...";
    },
    mediaTabTitle() {
      const mediaCount = this.conversationAttachments.length;
      return `Media (${mediaCount})`;
    },
    mediaTabIndex() {
      const tabIndex = this.getDrawerTabs.findIndex(tab => tab.type === 'attachments');
      return tabIndex >= 0 ? tabIndex : 0;
    },
    getDrawerTabs() {
      return this.drawerTabs
        .map(tab => {
          let enabled = false;
          if (tab.type === 'voice') {
            enabled = this.isSDKEnabled && this.agencySettings.uxVoiceEnabled;
          } else if (tab.type === 'video') {
            enabled = this.isSDKEnabled && this.agencySettings.uxVideoEnabled;
          } else {
            enabled = true;
          }
          return {
            ...tab,
            enabled,
          }
        })
        .filter(tab => tab.enabled);
    },
  },
  directives: {
    focus: {
      inserted(el) {
        el.focus();
      },
    },
  },
  created() {
    this.ttsActive = this.currentConversation.channel === "voice";
    this.fetchMessages(this.conversationId);

    setTimeout(() => {
      this.isDetailTabOpened = true;
      this.resizingDrawer = false;
      this.initOpenedChatTabSize();
    }, 500);

    this.unsubscribe = this.$store.subscribe((mutation) => {
      if (mutation.type === "conversations/addTranscription" && mutation.payload && mutation.payload.length > 0) {
        let inTxt, outTxt;
        for (var i = 0; i < mutation.payload.length; i++) {
          if (mutation.payload[i].direction === "inbound")
            inTxt = mutation.payload[i].text_trans ? mutation.payload[i].text_trans : mutation.payload[i].text;
          else if (mutation.payload[i].direction === "outbound")
            outTxt = mutation.payload[i].text_trans ? mutation.payload[i].text_trans : mutation.payload[i].text;
        }

        this.inboundTranscribeText = inTxt;
        this.outboundTranscribeText = outTxt;
      }
      else if (mutation.type === "conversations/addMessage") {
        if (mutation.payload.direction === "inbound")
          this.inboundTranscribeText = null;
        else if (mutation.payload.direction === "outbound")
          this.outboundTranscribeText = null;
      }
    });

    const self = this;
    setTimeout(function () {
      if (self.$refs.messageBox) {
        self.$refs.messageBox.focusTextarea();
        SocketHolder.socket.emit("joinConversation", self.conversationId);
      }
    }, 750);

    this.scrollToBottom();
  },
  methods: {
    ...mapActions("conversations", ["createMessage", "loadMessages", "sendMessage"]),
    messageAttachments(messageId) {
      const attachments = [...this.conversationAttachments];
      return attachments.sort((a, b) => a.created_at - b.created_at).filter(attachment => attachment.message_id == messageId);
    },
    getAttachmentWrapperWidth(message) {
      const { num_media: numMedia, message_id: messageId } = message;
      const attachments = this.messageAttachments(messageId);
      if (attachments.length > 0) {
        if (attachments.length == 1) {
          const type = this.getAttachmentType(attachments[0].content_type);
          return type === 'image' ? '200px' : '300px';
        } else {
          return '300px';
        }
      }
      return '200px';
    },
    getAttachmentWrapperType(message) {
      const { message_id: messageId } = message;
      const attachments = this.messageAttachments(messageId);
      const isMediaExist = attachments.filter(i => this.getAttachmentType(i.content_type) !== 'image').length > 0;
      return isMediaExist ? 'media' : 'image';
    },
    getAttachmentType(contentType) {
      if (contentType) {
        contentType = contentType.toLowerCase();
        if (contentType.includes("image")) {
          return 'image';
        } else if (contentType.includes("video")) {
          return 'media';
        } else if (contentType.includes("audio")) {
          // if (contentType.includes('3gp')) {
          //   return 'video';
          // }
          return 'media';
        }
      }
      return "unknown";
    },
    getLastLocationTime(t) {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const formattedTime = moment.utc(t, "x").tz(tz).fromNow();
        return formattedTime;
      } catch (e) {
        return t;
      }
    },
    formatMessage(msg) {
      try {
        return msg.replace(/\n/g, "<br />");
      } catch (e) {
        console.log(e);
        return msg;
      }
    },
    async submitMessage(message) {
      if (this.chatEnabled) {
        let msg = message;
        if (!msg) {
          msg = this.message;
          if (!msg || msg === "")
            return;
        }

        if (!this.conversationId) {
          alert("Error - Conversation Not Loaded");
          return;
        }
        this.httpPending = true;

        try {
          await this.sendMessage({message, ttsActive: this.ttsActive});
          this.httpPending = false;
          this.message = null;
        } catch (err) {
          const audio = new Audio("/assets/audio/glitch-in-the-matrix-600.ogg");
          audio.play();

          this.$toastr("error", "Message send error. Please try again");
          this.httpPending = false;
        }
      }
    },
    async fetchMessages(conversationId) {
      try {
        this.fetchAbortController = new AbortController();
        this.isLoading = true;
        const res = await this.$ConveyApi.conversationsApi.getMessagesForConversation({
          conversationId,
          signal: this.fetchAbortController.signal,
        });
        this.loadMessages(res.data.data);
        this.scrollToBottom();
      } catch(err) {
        if (!this.isApiCanceled(err)) {
          console.log("[ConversationChatContainer:fetchMessages] ERROR: ", err);
        }
      } finally {
        this.isLoading = false;
      }
    },  
    scrollToBottom() {
      const $ = window.$;
      window.setTimeout(() => $(".chat-box").scrollTop($(".chat-box").prop("scrollHeight")), 250);
      $(".chat-box").perfectScrollbar("update");
    },
    toggleConversationDetailTabs() {
      this.resizingDrawer = false;
      if (this.isDetailTabOpened) {
        this.initClosedChatTabSize();
      } else {
        this.initOpenedChatTabSize();
      }
      this.isDetailTabOpened = !this.isDetailTabOpened;
    },
    openTab(tab) {
      this.detailTabOpened = tab;
      if (!this.isDetailTabOpened) {
        this.toggleConversationDetailTabs();
      }
    },
    initClosedChatTabSize() {
      if (
        this.$refs.convoChatContainerRef &&
        this.$refs.convoChatContainerRef.clientHeight &&
        this.$refs.convoChatContainerRef.clientWidth
      ) {
        this.drawerWidth = this.$refs.convoChatContainerRef.clientWidth;
        this.drawerHeight = 40;
        this.chatBoxHeight = this.$refs.convoChatContainerRef.clientHeight - 41 - 50 - 40;
        this.drawerLeft = 0;
        this.drawerTop = this.chatBoxHeight + 41;
        this.drawerMaxHeight = this.$refs.convoChatContainerRef.clientHeight - 41 - 50 - 100;
      }
    },
    initOpenedChatTabSize() {
      if (
        this.$refs.convoChatContainerRef &&
        this.$refs.convoChatContainerRef.clientHeight &&
        this.$refs.convoChatContainerRef.clientWidth
      ) {
        this.drawerWidth = this.$refs.convoChatContainerRef.clientWidth;
        this.drawerHeight = (this.$refs.convoChatContainerRef.clientHeight - 41 - 50) / 2;
        this.chatBoxHeight = (this.$refs.convoChatContainerRef.clientHeight - 41 - 50) / 2;
        this.drawerLeft = 0;
        this.drawerTop = this.chatBoxHeight + 41;
        this.drawerMaxHeight = this.$refs.convoChatContainerRef.clientHeight - 41 - 50 - 100;
      }
    },
    vResizeHandler($event) {
      if (this.isDetailTabOpened) {
        const { eventName, height, top } = $event;
        if (eventName !== "resize:end") {
          this.resizingDrawer = true;
        } else {
          this.resizingDrawer = false;
        }
        if (height > 100) {
          if (height <= this.drawerMaxHeight) {
            this.drawerHeight = height;
            this.drawerTop = top;
            this.chatBoxHeight = this.$refs.convoChatContainerRef.clientHeight - 41 - 50 - height;
          }
        } else {
          this.isDetailTabOpened = false;
          this.initClosedChatTabSize();
        }
      }
    },
  },
  watch: {
    lastMessageAt: function () {
      this.scrollToBottom();
    },
    viewport: {
      handler(viewport) {
        if (this.isDetailTabOpened) {
          this.initOpenedChatTabSize()
        } else {
          this.initClosedChatTabSize();
        }
      },
      deep: true
    },
    userSidebarOpened(value) {
      if (this.isDetailTabOpened) {
        this.initOpenedChatTabSize()
      } else {
        this.initClosedChatTabSize();
      }
    },
  },
  beforeDestroy() {
    this.unsubscribe();
    if (this.fetchAbortController) {
      this.fetchAbortController.abort();
      this.fetchAbortController = null;
    }
  },
};
</script>

<style lang="scss">
.conversation-chat-container {
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;

  >div {
    height: 100%;
  }

  .card {
    height: 100%;
    margin: 0;
    border-radius: 1rem;
    border: none !important;

    .chat-header {
      width: 100%;
      height: 41px;
      max-height: 41px;
      min-height: 41px;
      border-radius: 1rem 1rem 0 0;
    }

    .chat-box {
      transition: height ease-in-out 0.15s;
      overflow-y: auto;

      .chat-content {
        width: 100%;
        overflow-x: auto;
      }

      >p {
        &:first-child {
          >ul {
            >li {
              >div {
                border: none !important;
              }
            }
          }
        }
      }

      div .chat-message-translated {
        color: #8f8f8f;
        margin-top: 10px;
      }

      .chat-message-translated-lang {
        font-size: small;
        font-style: italic;
        color: #8f8f8f;
      }

      .chat-content-attachments {
        &.image-attachments {
          .chat-content-attachment {
          }
        }

        &.media-attachments {
        }
      }
    }

    .chat-tabs-resizer {
      bottom: 0 !important;
      top: 0 !important;
      max-height: calc(100% - 100px);
    }

    .chat-tabs {
      display: grid;
      grid-template-rows: 40px 1fr;
      height: 100%;
      transition: height ease-in-out 0.15s;

      &.chat-tabs-rounded {
        border-radius: 0 0 1rem 1rem;
        .chat-tabs-content {
          border-radius: 0 0 1rem 1rem;
        }
        .chat-tabs-header.chat-tabs-header-rounded {
          border-radius: 0 0 1rem 1rem;
          overflow: hidden;
          .chat-tabs-header--tabs {
            width: 100%;
          }
        }
      }

      &-header {
        position: relative;

        &--trigger {
          position: absolute;
          top: 0;
          right: calc(50% - 16px);
          padding: 0 8px;
          cursor: pointer;
          border-radius: 0 0 4px 4px;
          z-index: 2;
          opacity: 0.7;

          &:hover {
            opacity: 1;
          }

          &:focus {
            opacity: 1;
          }
        }

        &--tabs {
          height: 100%;
          gap: 4px;
          padding: 0 4px;

          .chat-tabs-header--tab {
            width: auto;
            height: 100%;
            cursor: pointer;
            border-bottom: 2px solid transparent;
            padding: 8px;
            font-weight: normal;
            white-space: nowrap;

            &.tab-selected {
              // font-weight: bold;
            }

            @media screen and (max-width: 767px) {
              font-size: 12px;
              padding: 10px 4px;
            }
          }

          .chat-tabs-header--quick-reply {
            margin-left: auto;

            @media screen and (max-width: 767px) {
              font-size: 12px;
              padding: 10px 4px;
            }
          }
        }
      }

      &-content {
        width: 100%;
        height: 100%;
        overflow: hidden;
        overflow-y: auto;

        >.tabs {
          height: 100%;

          >div:first-child {
            display: none;
          }

          .tab-content {
            height: 100%;

            .tab-pane {
              height: 100%;

              div[id^=voice-sdk-container] {
                width: 100%;
                height: 100%;

                >div {
                  width: 100% !important;
                  height: 100% !important;
                }

                div.ConveyEmbed--ActionsContainer {
                  height: 40px !important;
                  border-top: none !important;
                }
                div.ConveyEmbed--TranscribeWrap {
                  position: unset !important;
                  width: 100%;
                  height: calc(100% - 40px) !important;
                  padding: 0 !important;
                }
              }
            }
          }
        }
      }
    }

    .chat-send-message-footer {
      height: 50px;
      max-height: 50px;
      min-height: 50px;
      border-radius: 0 0 1rem 1rem;

      >div {
        &:first-child {
          flex: 1
        }

        &:last-child {
          // width: 50px;
        }

        >.message-box {
          width: 100%;
        }

        >.message-send-btn {
          font-size: 24px;
          padding: 0;
        }
      }
    }
  }
}
</style>
